import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { withAsyncContainer } from '@mews-commander/ui';
import { ApplicationState, WithAccountId, CommanderThunkDispatch } from '@mews-commander/types';
import { AsyncMetaState, NumberFormatting, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    commanderForm,
    formattedCurrency,
    nonZeroAmount,
    positiveAmount,
    presence,
    upperBoundNumber,
    validation,
} from '@mews-commander/legacy-form';
import { getCurrentNumberCultureInfo } from '@mews-commander/core/legacy-globalization';
import { hasReadOnlyPrivilegesForCompanyData } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { getCreditLimitInitialValues, hasUpdateFailed, isUpdating } from '../selectors';
import { addOrUpdateCreditLimit } from '../actions';
import { UPPER_BOUND_VALUE } from '../constants';
import CreditLimitFields from './CreditLimitFields';

const getConstraints = ({ cultureInfo }: { cultureInfo: NumberFormatting }) => ({
    value: {
        nonZeroAmount,
        positiveAmount,
        formattedCurrency: {
            ...formattedCurrency,
            optional: true,
        },
        upperBoundNumber: upperBoundNumber({
            upperBound: UPPER_BOUND_VALUE,
            cultureInfo,
        }),
    },
    currencyCode: { presence },
});

export interface CreditLimitFormValues {
    value: string | null;
    currencyCode: string | null;
    creditRatingBasic: string | null;
}

export interface CreditLimitFormProps extends AsyncMetaState {
    initialValues: CreditLimitFormValues;
    cultureInfo: NumberFormatting;
    readOnlyForm: boolean;
}

interface OwnProps extends WithAccountId {
    form: string;
    strawberryReadOnlyFieldsEnabled: boolean;
}

function getReadOnlyFormState(state: ApplicationState, strawberryReadOnlyFieldsEnabled: boolean): boolean {
    return hasReadOnlyPrivilegesForCompanyData(state) || strawberryReadOnlyFieldsEnabled;
}

export default compose<React.FC<OwnProps>>(
    connect<CreditLimitFormProps, {}, OwnProps, ApplicationState>(
        createStructuredSelector({
            initialValues: (state: ApplicationState, { accountId }: WithAccountId) =>
                getCreditLimitInitialValues(state, { id: accountId, accountId }),
            cultureInfo: getCurrentNumberCultureInfo,
            isFetching: isUpdating,
            hasFailed: hasUpdateFailed,
            readOnlyForm: (state: ApplicationState, ownProps: OwnProps) =>
                getReadOnlyFormState(state, ownProps.strawberryReadOnlyFieldsEnabled),
        }),
        (dispatch: CommanderThunkDispatch, { accountId }) => ({
            onSubmit: async (values: CreditLimitFormValues) => dispatch(addOrUpdateCreditLimit(values, accountId)),
        })
    ),
    withAsyncContainer,
    commanderForm({
        enableReinitialize: true,
        validate: validation(getConstraints),
        submitLabel: <LocalizedText textKey={TextKey.Save} />,
        submitButtonProps: { variant: 'primary' },
    })
)(CreditLimitFields);
